import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
} from '@tanstack/react-query';
import React, { FC } from 'react';
import { Control, useController } from 'react-hook-form';
import InfiniteScroll from 'react-infinite-scroll-component';

import { ChevronDownIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import {
  ComboboxButton,
  ComboboxInput,
  ComboboxOptions,
  FormCombobox,
  FormComboboxOption,
} from '../../../../components/combobox/index';
import Loader from '../../../../components/loader/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { EventsResponse } from '../../../../hooks/event/index';
import { EventHooks, useScreenSize } from '../../../../hooks/index';
import { EventTypeBackend, SortByType, ValuationEvent } from '../../../../types/events.types';
import { defaultSharePrice, FormSchema } from '../event-form.validation';

type EventType = {
  id: string;
  issuedSharesOnStart: number;
  name: string;
  sharePrice: number;
};

export type EventsItemsListProps = {
  control: Control<FormSchema>;
  data: ValuationEvent[];
  fetchNextPage: (
    options?: FetchNextPageOptions,
  ) => Promise<InfiniteQueryObserverResult<InfiniteData<EventsResponse, unknown>, Error>>;
  fieldName: string;
  hasNextPage: boolean;
  lockMode: boolean;
  search: string;
};

const EventsItemsList: FC<EventsItemsListProps> = ({
  data,
  control,
  fieldName,
  search,
  lockMode,
  hasNextPage,
  fetchNextPage,
}) => {
  const { scrollableTarget } = useScreenSize();
  const latestValuationEventId = data.reduce<null | string>((latestId, event) => {
    if (!latestId) return event.id;
    const latestEvent = data.find(({ id }) => id === latestId);
    if (!latestEvent) return event.id;
    const currentCreatedAt = new Date(event.createdAt).getTime();
    const latestCreatedAt = new Date(latestEvent.createdAt).getTime();
    if (currentCreatedAt > latestCreatedAt) return event.id;
    if (currentCreatedAt === latestCreatedAt && event.id > latestEvent.id) return event.id;
    return latestId;
  }, null);

  return (
    <InfiniteScroll
      className="flex w-full flex-col gap-2"
      dataLength={data.length}
      hasMore={hasNextPage}
      loader={<Loader />}
      next={fetchNextPage}
      scrollableTarget={scrollableTarget}
    >
      {data.length > 0 ? (
        <>
          {data
            .sort((a, b) => {
              const aIsLatest = a.id === latestValuationEventId;
              const bIsLatest = b.id === latestValuationEventId;
              if (aIsLatest && !bIsLatest) return -1;
              if (!aIsLatest && bIsLatest) return 1;
              return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
            })
            .map(({ name, id, sharePrice, issuedSharesOnStart }, i) => {
              const isLatest = latestValuationEventId === id;

              if (!isLatest) {
                return (
                  <Tooltip
                    className="w-[450px]"
                    content={<AppFormattedMessage id="VALUATION_EVENT_PREVIOUS_PERIOD_LOCKED" />}
                    key={`${name}_${i}`}
                  >
                    <div className="w-full cursor-default rounded px-3 py-1.5 text-sm text-gray-400">
                      {name}
                    </div>
                  </Tooltip>
                );
              }

              return (
                <FormComboboxOption
                  className="w-full"
                  control={control}
                  key={`${name}_${i}`}
                  name={fieldName as keyof FormSchema}
                  value={
                    {
                      id,
                      name,
                      sharePrice,
                      issuedSharesOnStart,
                    } as unknown as FormSchema[keyof FormSchema]
                  }
                >
                  <Tooltip content={name}>
                    <span className="truncate text-sm text-gray-700">{name}</span>
                  </Tooltip>
                </FormComboboxOption>
              );
            })}
        </>
      ) : (
        <>
          <span className="ml-2 px-2 py-[6px] text-sm font-normal text-gray-700">
            <AppFormattedMessage id="NO_RESULTS_FOUND" />
          </span>
          {!lockMode && (
            <FormComboboxOption
              className="rounded bg-brand-25 px-2 py-[6px]"
              control={control}
              name={fieldName as keyof FormSchema}
              value={
                {
                  id: 'new',
                  name: search,
                  sharePrice: defaultSharePrice,
                } as FormSchema[keyof FormSchema]
              }
            >
              <span className="truncate text-start text-sm font-[450] text-brand-700">
                <AppFormattedMessage
                  id="CHOOSE_VALUATION_NAME"
                  values={{ valuationName: search }}
                />
              </span>
            </FormComboboxOption>
          )}
        </>
      )}
    </InfiniteScroll>
  );
};

export type EventComboboxProps = {
  companyId: string;
  control: Control<FormSchema>;
  disabled?: boolean;
  fieldName: string;
  lockMode: boolean;
  onlyWithSharePrice?: boolean;
};

export const EventCombobox: FC<EventComboboxProps> = ({
  control,
  companyId,
  fieldName,
  lockMode,
  disabled = false,
  onlyWithSharePrice = false,
}) => {
  const {
    events: backendEvents,
    fetchNextPage,
    hasNextPage,
  } = EventHooks.useEvents({
    companyId,
    typesFilter: [SortByType.VALUATION],
  });

  const { field, fieldState } = useController({
    control,
    name: fieldName as keyof FormSchema,
  });

  const value = (field?.value as EventType)?.name || '';
  // A missing id or share price has no field of its own on screen — surface it on the input the
  // user can actually reach instead of failing validation silently.
  const valuationError = fieldState.error as
    | { id?: { message?: string }; message?: string; name?: { message?: string } }
    | undefined;
  const valuationErrorMessage =
    valuationError?.name?.message ?? valuationError?.id?.message ?? valuationError?.message;

  if (!backendEvents) return;
  const filteredEvents = backendEvents
    .filter(
      (event): event is WithPermissions<ValuationEvent> =>
        event.type === EventTypeBackend.VALUATION,
    )
    .filter((event) => event.name.includes(value.trim()));

  // Typing used to replace the whole selection with `{ name }`, dropping the valuation id and its
  // share price. The combobox renders only the `name` error, so the resulting `event.id` failure
  // was invisible — Update toasted "fix the highlighted fields" with nothing to fix. Keep the
  // selected valuation attached; `handleBlur` still promotes an unmatched name to a new valuation.
  const onChange = (value: string) => {
    const selectedEvent = field.value as EventType | undefined;
    const matchedEvent = filteredEvents.find((event) => event.name === value);

    if (matchedEvent) {
      field.onChange({
        id: matchedEvent.id,
        name: matchedEvent.name,
        sharePrice: matchedEvent.sharePrice,
        issuedSharesOnStart: matchedEvent.issuedSharesOnStart,
      });
      return;
    }

    field.onChange({ ...selectedEvent, name: value });
  };

  const handleBlur = () => {
    if (!lockMode && value && !filteredEvents.some((event) => event.name === value)) {
      field.onChange({
        id: 'new',
        name: value,
        sharePrice: defaultSharePrice,
      });
    }
  };

  return (
    <>
      <FormCombobox
        className="relative w-full"
        control={control}
        name={fieldName as keyof FormSchema}
      >
        <div className="relative">
          <ComboboxInput
            {...field}
            disabled={disabled}
            errorMessage={valuationErrorMessage}
            icon={<ChevronDownIcon className="mt-3" />}
            onBlur={handleBlur}
            onChange={onChange}
            placeholder={
              fieldName === 'stepOne.eventDetails.fundraising-round.event' ? (
                <AppFormattedMessage id="ROUND_NAME" />
              ) : (
                <AppFormattedMessage id="EVENT_NAME" />
              )
            }
            value={value}
          />
          <ComboboxButton className="absolute top-0 z-1 h-full w-full" disabled={disabled} />
        </div>

        <ComboboxOptions className="w-full overflow-x-hidden">
          <EventsItemsList
            control={control}
            data={
              filteredEvents.filter(({ sharePrice }) =>
                onlyWithSharePrice ? sharePrice : true,
              ) as ValuationEvent[]
            }
            fetchNextPage={fetchNextPage}
            fieldName={fieldName}
            hasNextPage={hasNextPage}
            lockMode={lockMode}
            search={value}
          />
        </ComboboxOptions>
      </FormCombobox>
    </>
  );
};
